import useMultiStaticOptionsSelector from '@components/Crud/filters/hooks/multiOptionsSelectorFilter/useMultiStaticOptionsSelector';
import {
  FilterOptionV3,
  PossibleFilters,
} from '@components/Crud/filters/sharedBetweenFilters/types';
import { DateRangeFilterToFilterV3 } from '@components/FilterV3/components/DateRangeFilterV2/components/filter/components/DateRangeFilterToFilterV3';
import useDateRangeFilterWithDefaultValues from '@components/FilterV3/components/DateRangeFilterV2/components/filter/useDateRangeFilterWithDefaultValues';
import { DateShortcut } from '@components/FilterV3/components/DateRangeFilterV2/dateRangeFilter.types';
import FixedFilterChip from '@components/FilterV3/components/FixedFilterChip/FixedFilterChip';
import { ReactNode } from 'react';
import LangEnum from '../../../../../lang/LangEnum';
import { ActivityFeedDomain } from '../../../../../services/modules/activityFeed/activityFeed.types';
import { useTranslation } from '../../../../translation/TranslationContext';
import { ACTIVITY_FEED_FILTER_SEARCH_PARAMS } from '../../../activityFeed.constants';

const DOMAIN_LABEL: Record<ActivityFeedDomain, LangEnum> = {
  registration: LangEnum.GLOBAL_REGISTRATION,
  orders: LangEnum.GLOBAL_ORDERS,
  order_status: LangEnum.ORDER_STATUS_LABEL,
  integration: LangEnum.GLOBAL_INTEGRATIONS,
  targets: LangEnum.GLOBAL_PLANS,
};

const DOMAIN_FILTER_INDEX = 0;
const PERIOD_FILTER_INDEX = 1;

interface UseActivityFeedFilterBarParams {
  domains: ActivityFeedDomain[];
}

const useActivityFeedFilterBar = ({ domains }: UseActivityFeedFilterBarParams) => {
  const { translate } = useTranslation();

  const domainOptions = domains.map((domain) => ({
    value: domain,
    label: translate(DOMAIN_LABEL[domain]),
  }));

  const {
    cleanFilter: cleanDomainFilter,
    getSearchParamKeys: getDomainSearchParamKeys,
    ...domainFilter
  } = useMultiStaticOptionsSelector({
    key: ACTIVITY_FEED_FILTER_SEARCH_PARAMS.domain,
    staticOptions: domainOptions,
  });

  const periodFilter = useDateRangeFilterWithDefaultValues({
    defaultFilters: null,
    title: translate(LangEnum.GLOBAL_PERIOD),
    groupBy: LangEnum.GLOBAL_ACTIVITIES,
    systemDefaultShortcut: DateShortcut.ALL_PERIOD,
    filtersKey: {
      startDateKey: ACTIVITY_FEED_FILTER_SEARCH_PARAMS.from,
      endDateKey: ACTIVITY_FEED_FILTER_SEARCH_PARAMS.to,
    },
  });

  const options: FilterOptionV3[] = [
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_STATIC_DATA,
      title: translate(LangEnum.ACTIVITY_FEED_DOMAIN_FILTER_LABEL),
      hideSearchField: true,
      fixed: true,
      multiStaticOptionsArgs: domainFilter,
      cleanFilter: cleanDomainFilter,
      getSearchParamKeys: getDomainSearchParamKeys,
      groupBy: LangEnum.GLOBAL_ACTIVITIES,
    },
    periodFilter,
  ];

  const getDomainChipLabel = () => {
    const title = translate(LangEnum.ACTIVITY_FEED_DOMAIN_FILTER_LABEL);
    const selectedLabels = domainOptions
      .filter((option) => domainFilter.selectedOptions.includes(option.value))
      .map((option) => option.label);

    if (selectedLabels.length === 0) {
      return title;
    }

    if (selectedLabels.length === 1) {
      return `${title}: ${selectedLabels[0]}`;
    }

    return `${title}: ${selectedLabels.length} ${translate(LangEnum.GLOBAL_SELECTED_PLURAL)}`;
  };

  const fixedFilters: ReactNode[] = [
    <FixedFilterChip
      key={ACTIVITY_FEED_FILTER_SEARCH_PARAMS.domain}
      label={getDomainChipLabel()}
      filterIndex={DOMAIN_FILTER_INDEX}
    />,
    <DateRangeFilterToFilterV3
      key={ACTIVITY_FEED_FILTER_SEARCH_PARAMS.from}
      label={LangEnum.GLOBAL_PERIOD}
      filterIndex={PERIOD_FILTER_INDEX}
      dateRangeFilter={periodFilter.dateRangeArgs}
    />,
  ];

  return { options, fixedFilters };
};

export default useActivityFeedFilterBar;
